import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { useAppSelector } from '../../../app/hooks';
import { EXPORT_EVIDENCE_PERMISSION } from '../../../services/assessments';

interface ExportDownloadConfirmDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleDownload: (includeEvidence: boolean) => void;
}

const ExportDownloadConfirmDialog: React.FC<ExportDownloadConfirmDialogProps> = ({
  isOpen,
  handleClose,
  handleDownload,
}) => {
  const { user } = useAppSelector((state) => state.user);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (isOpen) setAgreed(false);
  }, [isOpen]);

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '';
  const canIncludeEvidence = Boolean(user?.permissions?.includes(EXPORT_EVIDENCE_PERMISSION));

  return (
    <StandardDialog
      isOpen={isOpen}
      handleClose={handleClose}
      title="Confirm Export Download"
      maxWidth="sm"
    >
      <Typography>
        This export contains sensitive assessment data{canIncludeEvidence ? ' and, if included, evidence files' : ''}.
        Before providing it to an <strong>approved AI tool*</strong>, ensure the tool is configured
        NOT to train on or retain this data.
      </Typography>
      <FormControlLabel
        sx={{ mt: 1 }}
        control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
        label={<>I, <strong>{userName}</strong>, agree</>}
      />
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
        * MPA AI Committee have reviewed and approved Microsoft CoPilot under corporate license
        for processing this data.
      </Typography>
      <StandardDialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => handleDownload(false)} disabled={!agreed}>
          Download
        </Button>
        {canIncludeEvidence && (
          <Button variant="contained" onClick={() => handleDownload(true)} disabled={!agreed}>
            Download with Evidence
          </Button>
        )}
      </StandardDialogActions>
    </StandardDialog>
  );
};

export default ExportDownloadConfirmDialog;
